import { Component } from '@theme/component';
import { Fleet } from '@theme/fleet';

/**
 * <fleet-cascade> — Year → Machine → Model dropdowns that SAVE the chosen
 * machine to the Fleet store (instead of navigating to /pages/fitment-search
 * like the hero's <ymm-cascade>). Reuses the .ymm-cascade__* styles (inlined
 * globally in layout/theme.liquid) and the same ymm-cascade.json dataset.
 *
 * The JSON is fetched once per page and shared across every instance (the
 * header drawer and the /pages/fleet page can both render one).
 *
 * Requires all three of year/machine/model so saved machines are complete and
 * the PDP fit badge can match them reliably.
 */

let cachePromise = null;

function loadData(src) {
  if (!cachePromise) {
    cachePromise = fetch(src).then((r) => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    });
  }
  return cachePromise;
}

class FleetCascade extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.yearSel = this.querySelector('[data-field="year"]');
    this.machineSel = this.querySelector('[data-field="machine"]');
    this.modelSel = this.querySelector('[data-field="model"]');
    this.submitBtn = this.querySelector('[type="submit"]');
    this.form = this.querySelector('form');

    if (!this.yearSel || !this.machineSel || !this.modelSel || !this.form) return;

    this.yearSel.addEventListener('change', () => this.#onYear());
    this.machineSel.addEventListener('change', () => this.#onMachine());
    this.modelSel.addEventListener('change', () => this.#update());
    this.form.addEventListener('submit', (e) => this.#onSubmit(e));

    // The dataset is ~474KB raw (~40KB wire) and the header-drawer instance
    // renders on EVERY page inside a closed <dialog> — don't fetch+parse it
    // just because the element exists. Triggers, earliest first: the host
    // dialog opening (head start before the user reaches the add panel),
    // the element becoming visible (add panel expanded in the drawer;
    // scrolled into view / anchor-jumped on /pages/fleet), and a
    // focus/pointerdown fallback so the Year select can never be interacted
    // with before a load has been kicked off.
    this.#armLazyLoad();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.yearGuard?.disconnect();
    this.yearGuard = null;
    this.visGuard?.disconnect();
    this.visGuard = null;
  }

  #armLazyLoad() {
    const start = () => this.#startLoad();
    if (!('IntersectionObserver' in window)) {
      start();
      return;
    }
    this.visGuard = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) start();
    });
    this.visGuard.observe(this);
    this.closest('dialog')?.addEventListener('toggle', start, { once: true });
    this.yearSel.addEventListener('focus', start, { once: true });
    this.yearSel.addEventListener('pointerdown', start, { once: true });
  }

  #startLoad() {
    this.visGuard?.disconnect();
    this.visGuard = null;
    if (this.loadStarted) {
      // Re-entry after a same-instance disconnect/reconnect (Horizon's header
      // morph does this on every page): the MutationObserver guard was torn
      // down, so re-run the idempotent populate/guard pair to self-heal the
      // Year options.
      if (this.data) {
        this.#populateYears();
        this.#guardYears();
      }
      return;
    }
    this.loadStarted = true;
    // Slow-network affordance: the select is interactive while the ~40KB
    // dataset is in flight, so make the placeholder say what's happening.
    const placeholder = this.yearSel.options[0];
    const placeholderText = placeholder ? placeholder.textContent : '';
    if (placeholder) placeholder.textContent = 'Loading years…';
    loadData(this.dataset.src)
      .then((data) => {
        this.data = data;
        if (placeholder) placeholder.textContent = placeholderText;
        this.#populateYears();
        this.#guardYears();
      })
      .catch((err) => {
        if (placeholder) placeholder.textContent = placeholderText;
        console.error('fleet-cascade data load failed', err);
      });
  }

  /**
   * Build the Year options from the dataset keys. Idempotent — bails if they are
   * already present — so it is safe to call again after a re-render.
   */
  #populateYears() {
    if (!this.data || !this.yearSel || this.yearSel.options.length > 1) return;
    const years = Object.keys(this.data).sort((a, b) => Number(b) - Number(a));
    const frag = document.createDocumentFragment();
    for (const y of years) frag.append(new Option(y, y));
    this.yearSel.append(frag);
  }

  /**
   * Horizon hydrates/morphs sections after first paint, reconciling this <select>
   * back to its server-rendered HTML (placeholder only) and silently dropping the
   * options we add in JS. Re-populate whenever the option list is reset so the
   * Year dropdown self-heals; the idempotent guard above prevents a feedback loop.
   */
  #guardYears() {
    if (this.yearGuard || !this.yearSel) return;
    this.yearGuard = new MutationObserver(() => this.#populateYears());
    this.yearGuard.observe(this.yearSel, { childList: true });
  }

  #reset(select, placeholder) {
    select.innerHTML = '';
    select.add(new Option(placeholder, ''));
    select.disabled = true;
  }

  #onYear() {
    this.#reset(this.machineSel, 'Select machine');
    this.#reset(this.modelSel, 'Select model');
    this.#update();

    const year = this.yearSel.value;
    if (!year || !this.data[year]) return;
    for (const m of Object.keys(this.data[year]).sort()) this.machineSel.add(new Option(m, m));
    this.machineSel.disabled = false;
  }

  #onMachine() {
    this.#reset(this.modelSel, 'Select model');
    this.#update();

    const year = this.yearSel.value;
    const machine = this.machineSel.value;
    if (!year || !machine || !this.data[year] || !this.data[year][machine]) return;
    for (const model of this.data[year][machine]) this.modelSel.add(new Option(model, model));
    this.modelSel.disabled = false;
  }

  #update() {
    this.submitBtn.disabled = !(this.yearSel.value && this.machineSel.value && this.modelSel.value);
  }

  #onSubmit(e) {
    e.preventDefault();
    const year = this.yearSel.value;
    const machine = this.machineSel.value;
    const model = this.modelSel.value;
    if (!year || !machine || !model) return;

    const id = Fleet.add({ year, machine, model });

    // Reset back to a clean state so the next add starts fresh.
    this.yearSel.value = '';
    this.#reset(this.machineSel, 'Select machine');
    this.#reset(this.modelSel, 'Select model');
    this.#update();

    // Let the host (drawer / page) react — e.g. collapse the add panel.
    this.dispatchEvent(new CustomEvent('fleet:added', { bubbles: true, detail: { id } }));
  }
}

if (!customElements.get('fleet-cascade')) customElements.define('fleet-cascade', FleetCascade);
